import React, { useState } from "react";
import { useLanguage } from "@/App";
import { 
  Shield, 
  Zap, 
  Clock, 
  Server, 
  RefreshCw, 
  Headphones,
  Check,
  ArrowRight,
  Star,
  Lock,
  Globe,
  HardDrive,
  CreditCard,
  Loader2,
  ShieldCheck
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAINTENANCE_IDS = ["maintenance_basis", "maintenance_pro", "maintenance_enterprise"];

const MaintenancePackages = () => {
  const { language } = useLanguage();
  const [loadingPkg, setLoadingPkg] = useState(null);

  const handleCheckout = async (packageId) => {
    setLoadingPkg(packageId);
    try {
      const response = await fetch(`${API}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });
      if (!response.ok) throw new Error("Checkout failed");
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      toast.error(language === "nl" ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong.");
    } finally {
      setLoadingPkg(null);
    }
  };

  const packages = [
    {
      name: "Basis",
      price: "€99",
      color: "border-zinc-700",
      accent: "text-zinc-400",
      badge: null,
      description_en: "Essential maintenance for small business websites",
      description_nl: "Essentieel onderhoud voor kleine bedrijfswebsites",
      features_en: [
        "Monthly software updates",
        "Weekly backups",
        "SSL certificate management",
        "Uptime monitoring",
        "5GB hosting storage",
        "Email support (48h response)",
        "Basic security scanning",
        "1 minor content update/month"
      ],
      features_nl: [
        "Maandelijkse software updates",
        "Wekelijkse backups",
        "SSL certificaat beheer",
        "Uptime monitoring",
        "5GB hosting opslag",
        "E-mail support (48u reactie)",
        "Basis beveiligingsscan",
        "1 kleine content update/maand"
      ]
    },
    {
      name: "Pro",
      price: "€199",
      color: "border-[#FF6B00]",
      accent: "text-[#FF6B00]",
      badge: language === "nl" ? "Meest Gekozen" : "Most Popular",
      description_en: "Complete care package for growing businesses",
      description_nl: "Compleet verzorgingspakket voor groeiende bedrijven",
      features_en: [
        "Weekly software updates",
        "Daily backups",
        "SSL & domain management",
        "24/7 uptime monitoring",
        "25GB hosting storage",
        "Priority email support (24h)",
        "Advanced security & firewall",
        "3 content updates/month",
        "Performance optimization",
        "Monthly analytics report"
      ],
      features_nl: [
        "Wekelijkse software updates",
        "Dagelijkse backups",
        "SSL & domein beheer",
        "24/7 uptime monitoring",
        "25GB hosting opslag",
        "Prioriteit e-mail support (24u)",
        "Geavanceerde beveiliging & firewall",
        "3 content updates/maand",
        "Prestatie optimalisatie",
        "Maandelijks analytics rapport"
      ]
    },
    {
      name: "Enterprise",
      price: "€399",
      color: "border-purple-500/50",
      accent: "text-purple-400",
      badge: null,
      description_en: "Premium managed hosting & support for established businesses",
      description_nl: "Premium managed hosting & support voor gevestigde bedrijven",
      features_en: [
        "Real-time software updates",
        "Real-time backups & disaster recovery",
        "Multi-domain management",
        "24/7 uptime with instant alerts",
        "100GB+ hosting storage",
        "Phone & chat support (1h response)",
        "Enterprise-grade security suite",
        "Unlimited content updates",
        "CDN & performance tuning",
        "Dedicated account manager",
        "Quarterly strategy review",
        "Priority development queue"
      ],
      features_nl: [
        "Real-time software updates",
        "Real-time backups & disaster recovery",
        "Multi-domein beheer",
        "24/7 uptime met directe alerts",
        "100GB+ hosting opslag",
        "Telefoon & chat support (1u reactie)",
        "Enterprise-grade beveiligingssuite",
        "Onbeperkte content updates",
        "CDN & prestatie tuning",
        "Toegewijde account manager",
        "Kwartaal strategie review",
        "Prioriteit development wachtrij"
      ]
    }
  ];

  const highlights = [
    { icon: Shield, label_en: "99.9% Uptime", label_nl: "99.9% Uptime" },
    { icon: Lock, label_en: "SSL Secured", label_nl: "SSL Beveiligd" },
    { icon: HardDrive, label_en: "Daily Backups", label_nl: "Dagelijkse Backups" },
    { icon: Globe, label_en: "CDN Included", label_nl: "CDN Inbegrepen" }
  ];

  const getDesc = (p) => language === "nl" ? p.description_nl : p.description_en;
  const getFeatures = (p) => language === "nl" ? p.features_nl : p.features_en;

  return (
    <section
      id="maintenance"
      data-testid="maintenance-section"
      className="py-32 bg-gradient-to-b from-[#030303] via-[#080808] to-[#030303] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(255,107,0,0.08),transparent_60%)]" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,107,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.05) 1px, transparent 1px)`,
            backgroundSize: '80px 80px'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-5 py-2 mb-6">
            <RefreshCw size={16} className="text-[#FF6B00]" />
            <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Hosting & Onderhoud" : "Hosting & Maintenance"}
            </span>
          </span>
          <h2
            data-testid="maintenance-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Altijd " : "Always "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              {language === "nl" ? "Online & Veilig" : "Online & Secure"}
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl"
              ? "Geen zorgen meer over je website. Wij regelen hosting, updates, beveiliging en support zodat jij kunt focussen op je business."
              : "No more worrying about your website. We handle hosting, updates, security and support so you can focus on your business."}
          </p>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-6 mb-16"
        >
          {highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-zinc-400">
              <h.icon size={18} className="text-[#FF6B00]" />
              <span className="text-sm font-medium">{language === "nl" ? h.label_nl : h.label_en}</span>
            </div>
          ))}
        </motion.div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`maintenance-package-${pkg.name.toLowerCase()}`}
              className={`relative bg-[#0A0A0A] p-8 md:p-10 flex flex-col ${
                index === 1 ? "border border-[#FF6B00] shadow-[0_0_30px_rgba(255,107,0,0.1)]" : ""
              }`}
            >
              {/* Badge */}
              {pkg.badge && (
                <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-[#FF6B00] text-white text-xs font-bold uppercase tracking-wider px-4 py-1">
                  {pkg.badge}
                </div>
              )}

              {/* Package Name */}
              <h3
                className="text-xl font-bold text-white mb-2"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {pkg.name}
              </h3>

              {/* Price */}
              <div className="mb-4">
                <span className={`text-4xl md:text-5xl font-black ${index === 1 ? "text-[#FF6B00]" : "text-white"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {pkg.price}
                </span>
                <span className="text-zinc-500 text-sm ml-1">/{language === "nl" ? "maand" : "month"}</span>
              </div>

              {/* Description */}
              <p className="text-zinc-400 text-sm mb-6">{getDesc(pkg)}</p>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {getFeatures(pkg).map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check size={16} className={`mt-0.5 flex-shrink-0 ${index === 1 ? "text-[#FF6B00]" : "text-zinc-500"}`} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="space-y-2">
                <button
                  onClick={() => handleCheckout(MAINTENANCE_IDS[index])}
                  disabled={loadingPkg === MAINTENANCE_IDS[index]}
                  data-testid={`maintenance-buy-${pkg.name.toLowerCase()}`}
                  className={`w-full group inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold tracking-wide transition-all disabled:opacity-50 ${
                    index === 1
                      ? "bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 hover:shadow-[0_0_30px_rgba(255,107,0,0.3)]"
                      : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                  }`}
                >
                  {loadingPkg === MAINTENANCE_IDS[index] ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {language === "nl" ? "Laden..." : "Loading..."}
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} />
                      {language === "nl" ? "Direct Bestellen" : "Buy Now"}
                    </>
                  )}
                </button>
                <a
                  href="#contact"
                  data-testid={`maintenance-cta-${pkg.name.toLowerCase()}`}
                  className="w-full group inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-zinc-400 hover:text-white transition-all"
                >
                  {language === "nl" ? "Eerst Overleggen" : "Talk First"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MaintenancePackages;
